import React, {
  FC,
  PropsWithChildren,
  ReactNode,
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import clsx from 'clsx'
import usePrevious from '@ancon/wildcat-utils/shared/hooks/usePrevious'

import Portal from '../Portal'
import { PortalHostId } from '../../constants'

import ModalCard from './ModalCard'
import { ModalHeaderProps } from './ModalHeader'
import styles from './Modal.module.scss'

type ModalDisplayMode = 'modal' | 'full-screen' | 'bottom-sheet'

type ModalProps = PropsWithChildren<{
  isOpen?: boolean
  hideClose?: boolean
  backdropFade?: boolean
  mobileModalMode?: ModalDisplayMode
  onClose?: () => void
  title?: string
  className?: string
  contentClassName?: string
  CustomModalHeader?: FC<ModalHeaderProps>
  preventOnClose?: boolean
  modalHeaderClassName?: string
  /**
   * Pins the body while the modal is open on mobile so touch gestures can't
   * drag the page behind the modal or trigger pull-to-refresh
   */
  pinBodyOnMobile?: boolean
  /** Small line above the title, e.g. a tag */
  eyebrow?: ReactNode
  /** Line below the title */
  subtitle?: ReactNode
  /** Actions pinned to the bottom of the modal while the body scrolls */
  footer?: ReactNode
}>

function ModalComponent({
  children,
  isOpen = false,
  hideClose = false,
  backdropFade = true,
  mobileModalMode = 'modal',
  onClose,
  title,
  className,
  contentClassName,
  CustomModalHeader,
  preventOnClose = false,
  modalHeaderClassName,
  pinBodyOnMobile = false,
  eyebrow,
  subtitle,
  footer,
}: ModalProps) {
  const modalRef = useRef<HTMLDivElement>(null)

  const prevOpened = usePrevious(isOpen, true)

  const [closing, setClosing] = useState<boolean>(false)

  const bottomSheet = mobileModalMode === 'bottom-sheet'
  const fullScreen = mobileModalMode === 'full-screen'

  function restoreScrollbarIfNeeded() {
    if (document.body.classList.contains(styles.modalOpen)) {
      document.body.classList.remove(styles.modalOpen)
    }
  }

  function hideScrollbarIfNeeded() {
    if (!document.body.classList.contains(styles.modalOpen)) {
      document.body.classList.add(styles.modalOpen)
    }
  }

  const validateScrollbar = useCallback(() => {
    const hasChildNodes = document
      .getElementById(PortalHostId.Modal)
      ?.hasChildNodes()

    if (hasChildNodes) {
      hideScrollbarIfNeeded()
    } else {
      restoreScrollbarIfNeeded()
    }
  }, [])

  useEffect(() => {
    validateScrollbar()

    return () => validateScrollbar()
  }, [validateScrollbar])

  useEffect(() => {
    const didOpenChange = prevOpened !== isOpen

    if (didOpenChange) {
      validateScrollbar()
    }
  }, [isOpen, prevOpened, validateScrollbar])

  useEffect(() => {
    if (!pinBodyOnMobile || !isOpen) {
      return undefined
    }

    document.body.classList.add(styles.modalPinned)

    return () => {
      document.body.classList.remove(styles.modalPinned)
    }
  }, [pinBodyOnMobile, isOpen])

  function handleClose() {
    setClosing(true)
  }

  useEffect(() => {
    if (!isOpen || preventOnClose) {
      return undefined
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key !== 'Escape' || event.defaultPrevented) {
        return
      }

      // Stacked modals share the portal host in the order they opened, so
      // only the last one, the one on top, closes
      const modal = modalRef.current

      if (modal && modal.parentElement?.lastElementChild !== modal) {
        return
      }

      setClosing(true)
    }

    // On window, so an Escape handled inside the modal first, e.g. closing a
    // popover, can call preventDefault to keep the modal open
    window.addEventListener('keydown', handleKeyDown)

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen, preventOnClose])

  useEffect(() => {
    function handlePopState() {
      restoreScrollbarIfNeeded()
      if (isOpen) {
        onClose?.()
      }
    }

    window.addEventListener('popstate', handlePopState)

    return () => {
      window.removeEventListener('popstate', handlePopState)
    }
  }, [onClose, isOpen])

  function handleModalTransitionEnd(e: React.TransitionEvent<HTMLDivElement>) {
    if (e.target === modalRef.current) {
      e.stopPropagation()
      setClosing(false)

      restoreScrollbarIfNeeded()
      onClose?.()
    }
  }

  function handleModalClick(e: React.MouseEvent<HTMLDivElement, MouseEvent>) {
    e.stopPropagation()
    handleClose()
  }

  return (
    <Portal hostId={PortalHostId.Modal}>
      {isOpen && (
        <div
          ref={modalRef}
          className={clsx(
            styles.modal,
            {
              [styles.bottomSheetModal]: bottomSheet,
              [styles.modalFadeOut]: closing,
              [styles.backdropFade]: backdropFade,
            },
            className,
          )}
          onTransitionEnd={handleModalTransitionEnd}
        >
          <div
            className={styles.backdrop}
            onClick={preventOnClose ? undefined : handleModalClick}
            role="presentation"
          />
          <ModalCard
            title={title}
            onClose={hideClose || preventOnClose ? undefined : handleClose}
            className={clsx(
              closing && styles.cardScaleOut,
              closing && {
                [styles.fullScreenFadeOut]: fullScreen,
                [styles.bottomSheetSlideInDown]: bottomSheet,
              },
              contentClassName,
            )}
            mobileModalMode={mobileModalMode}
            CustomModalHeader={CustomModalHeader}
            modalHeaderClassName={modalHeaderClassName}
            eyebrow={eyebrow}
            subtitle={subtitle}
            footer={footer}
          >
            {children}
          </ModalCard>
        </div>
      )}
    </Portal>
  )
}

const Modal = memo(ModalComponent)

export type { ModalProps, ModalDisplayMode }

export default Modal
